import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { OpfsManager } from '../lib/opfs-manager';
import { clearSiteMarkers } from '../lib/opfs-storage-keys';
import { GradientBorder } from '../components/ui/gradient-border';
import { AspectRatio } from '../components/ui/aspect-ratio';
import { UserMenu } from '../components/UserMenu';
import { MilesLogo } from '../components/MilesLogo';
import { Button } from '../components/ui/button';
import { EditableTitle } from '../components/ui/editable-title';
import { PulsingDot } from '../components/ui/pulsing-dot';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plus,
  MoreVertical,
  Trash2,
  RefreshCw,
  AlertCircle,
  Palette,
  Copy,
} from 'lucide-react';
import { TryAnotherDesignModal } from '../components/TryAnotherDesignModal';
import { MobileNotice } from '../components/MobileNotice';
import { BetaWidget } from '../components/BetaWidget';
import { formatRelativeTime } from '../utils/format-relative-time';
import { API_URL } from '../lib/api';

interface HeroPreview {
  heroId: string;
  previewUrl: string;
  directionName: string | null;
}

interface Site {
  id: string;
  name: string;
  url: string;
  createdAt: string;
  lastSeenAt: string | null;
  isActive: boolean;
  accountId?: string | null;
  accessRole?: 'owner' | 'admin' | 'member';
  // Preview data
  heroPreviews?: HeroPreview[];
  selectedHeroId?: string;
  siteSlug?: string;
  messageCount?: number;
  agentManaged?: boolean;
}

interface UserProfile {
  id: string;
  email: string;
  name: string;
  termsAcceptedAt: string | null;
  milesAccountId: string | null;
  personalWorkspaceEnabled: boolean;
}

/**
 * Full site or selected hero preview iframe
 */
function SitePreview({
  siteSlug,
  selectedHeroId,
  heroPreviews,
}: {
  siteSlug?: string;
  selectedHeroId: string;
  heroPreviews?: HeroPreview[];
}): JSX.Element {
  let previewUrl: string;

  if (siteSlug) {
    previewUrl = `${API_URL}/preview/${siteSlug}/index.html`;
  } else {
    const selectedHero = heroPreviews?.find((h) => h.heroId === selectedHeroId);
    if (selectedHero) {
      previewUrl = `${API_URL}${selectedHero.previewUrl}`;
    } else {
      return <div className="w-full h-full bg-neutral-800" />;
    }
  }

  return (
    <div className="relative w-full h-full overflow-hidden bg-neutral-800">
      <iframe
        src={previewUrl}
        className="absolute inset-0 w-[400%] h-[400%] origin-top-left scale-[0.25] pointer-events-none"
        title="Site preview"
        loading="lazy"
      />
    </div>
  );
}

/**
 * 2x2 grid of hero preview iframes
 */
function HeroGridPreview({
  heroPreviews,
}: {
  heroPreviews: HeroPreview[];
}): JSX.Element {
  const heroes = heroPreviews.slice(0, 4);

  return (
    <div className="w-full h-full grid grid-cols-2 grid-rows-2 gap-1">
      {heroes.map((hero) => (
        <div
          key={hero.heroId}
          className="relative overflow-hidden rounded border border-white/15 bg-neutral-800"
        >
          <iframe
            src={`${API_URL}${hero.previewUrl}`}
            className="absolute inset-0 w-[400%] h-[400%] origin-top-left scale-[0.25] pointer-events-none"
            title={`Hero preview ${hero.heroId}`}
            loading="lazy"
          />
        </div>
      ))}
      {Array.from({ length: 4 - heroes.length }).map((_, i) => (
        <div
          key={`empty-${i}`}
          className="rounded border border-white/15 bg-neutral-800"
        />
      ))}
    </div>
  );
}

/**
 * Placeholder with unique layered gradient for sites without previews
 */
function PlaceholderPreview({
  name,
  siteId,
}: {
  name: string;
  siteId: string;
}): JSX.Element {
  // Generate unique values from ID
  let hash = 0;
  for (let i = 0; i < siteId.length; i++) {
    hash = siteId.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Generate two distinct hues for variety
  const hue1 = Math.abs(hash % 360);
  const hue2 = (hue1 + 60 + Math.abs((hash >> 8) % 60)) % 360;
  const angle = Math.abs((hash >> 4) % 360);

  // Layered gradient with mesh-like effect
  const background = `
    linear-gradient(${angle}deg, hsl(${hue1}, 50%, 15%) 0%, transparent 50%),
    linear-gradient(${(angle + 120) % 360}deg, hsl(${hue2}, 40%, 12%) 0%, transparent 50%),
    radial-gradient(ellipse at ${30 + (hash % 40)}% ${20 + ((hash >> 4) % 40)}%, hsl(${hue1}, 60%, 20%) 0%, transparent 50%),
    radial-gradient(ellipse at ${60 + ((hash >> 8) % 30)}% ${50 + ((hash >> 12) % 40)}%, hsl(${hue2}, 50%, 18%) 0%, transparent 40%),
    linear-gradient(to bottom right, hsl(${hue1}, 30%, 8%), hsl(${hue2}, 25%, 6%))
  `;

  return (
    <div
      className="w-full h-full relative overflow-hidden"
      style={{ background }}
    >
      <span className="absolute -bottom-4 -right-2 text-[100px] font-black leading-none text-white/[0.06] select-none">
        {name.charAt(0).toUpperCase()}
      </span>
    </div>
  );
}

/**
 * Site tile component
 */
function SiteTile({
  site,
  isDuplicating,
  duplicatePercent,
  isNew,
  onClick,
  onDelete,
  onRename,
  onDuplicate,
  onTryAnotherDesign,
}: {
  site: Site;
  isDuplicating: boolean;
  duplicatePercent?: number;
  isNew: boolean;
  onClick: () => void;
  onDelete: () => void;
  onRename: (newName: string) => void;
  onDuplicate: () => void;
  onTryAnotherDesign: () => void;
}): JSX.Element {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const lastEdited = site.lastSeenAt || site.createdAt;
  const canManageSite = site.accessRole !== 'member';

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    onDelete();
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <GradientBorder
        className={`group transition-all duration-500 ${canManageSite ? 'cursor-pointer' : ''} ${isNew ? 'animate-pop-in' : ''}`}
        innerClassName="overflow-hidden p-4 relative"
        onClick={isDuplicating || !canManageSite ? undefined : onClick}
      >
        {/* Duplicating overlay */}
        {isDuplicating && (
          <div className="absolute inset-0 z-10 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center rounded-[11px] gap-1">
            <PulsingDot statusText="Duplicating..." className="py-0" />
            {duplicatePercent != null && duplicatePercent > 0 && (
              <span className="text-white/40 text-xs">{duplicatePercent}%</span>
            )}
          </div>
        )}

        {/* Preview area */}
        <div className="relative">
          <AspectRatio ratio={16 / 10} className="rounded-lg overflow-hidden">
            {site.selectedHeroId ? (
              <SitePreview
                siteSlug={site.siteSlug}
                selectedHeroId={site.selectedHeroId}
                heroPreviews={site.heroPreviews}
              />
            ) : site.heroPreviews && site.heroPreviews.length > 0 ? (
              <HeroGridPreview heroPreviews={site.heroPreviews} />
            ) : (
              <PlaceholderPreview name={site.name} siteId={site.id} />
            )}
          </AspectRatio>
        </div>

        {/* Info area */}
        <div className="pt-4">
          {/* Title row - fixed height for consistency */}
          <div className="h-7">
            <EditableTitle
              title={site.name}
              onSave={onRename}
              disabled={!canManageSite}
              className="text-base font-medium text-white leading-tight min-w-0"
            />
          </div>

          {/* Bottom row: message count + time + menu */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-white/40 flex items-center gap-1.5">
              {site.messageCount != null && site.messageCount > 0
                ? `${site.messageCount.toLocaleString()} ${site.messageCount === 1 ? 'message' : 'messages'}`
                : 'No messages yet'}
            </span>
            <div className="flex items-center gap-1">
              {site.accessRole === 'member' && (
                <span className="rounded border border-white/10 bg-white/5 px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wider text-white/45">
                  Shared
                </span>
              )}
              <span className="text-xs text-white/40">
                {formatRelativeTime(lastEdited)}
              </span>
              {canManageSite && (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    className="p-1 rounded-md hover:bg-white/10 text-white/40 hover:text-white/60"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {site.heroPreviews &&
                      site.heroPreviews.length > 1 &&
                      site.selectedHeroId && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            onTryAnotherDesign();
                          }}
                        >
                          <Palette className="h-4 w-4 shrink-0 mt-0.5" />
                          Try another design
                        </DropdownMenuItem>
                      )}
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onDuplicate();
                      }}
                    >
                      <Copy className="h-4 w-4 shrink-0 mt-0.5" />
                      Duplicate Site
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleDelete}
                      className="text-red-400 focus:text-red-400 focus:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4 shrink-0 mt-0.5" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
          {!canManageSite && (
            <p className="mt-2 text-xs text-white/35">
              Ask an admin for editing access.
            </p>
          )}
        </div>
      </GradientBorder>

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete site?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{site.name}" and all its data. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/**
 * Page header with title and new site button
 */
function SitesGridHeader({
  onNewSite,
  disabled,
  user,
}: {
  onNewSite: () => void;
  disabled: boolean;
  user: {
    email?: string | null;
    name?: string | null;
    image?: string | null;
  } | null;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between shrink-0">
      <div className="flex items-center gap-2 text-sm">
        <MilesLogo />
        <span className="text-white/30">/</span>
        <span className="font-medium text-white">Sites</span>
      </div>
      <div className="flex items-center gap-2">
        <Button onClick={onNewSite} disabled={disabled}>
          <Plus className="w-4 h-4" />
          New Site
        </Button>
        {/* User menu */}
        {user && (
          <div className="ml-2">
            <UserMenu user={user} />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Empty state for new users - hero treatment
 */
function EmptyState({
  onNewSite,
  disabled,
  personalWorkspaceEnabled,
}: {
  onNewSite: () => void;
  disabled: boolean;
  personalWorkspaceEnabled: boolean;
}): JSX.Element {
  if (!personalWorkspaceEnabled) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <h2 className="text-2xl font-semibold text-white mb-2">
          No shared sites yet
        </h2>
        <p className="text-white/60 text-center max-w-md">
          Sites shared with you by your agency will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-full">
      <h2 className="text-2xl font-semibold text-white mb-2">
        Create your first site
      </h2>
      <p className="text-white/60 text-center max-w-md mb-8">
        Describe your vision and Miles will generate a beautiful, custom website
        for you in minutes.
      </p>
      <Button onClick={onNewSite} disabled={disabled}>
        <Plus className="w-4 h-4" />
        New Site
      </Button>
    </div>
  );
}

/**
 * Error state with retry button
 */
function SitesGridError({
  error,
  onRetry,
}: {
  error: Error;
  onRetry: () => void;
}): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-4">
      <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center">
        <AlertCircle className="w-7 h-7 text-red-400" />
      </div>
      <div className="text-center">
        <p className="text-base font-medium text-white">Failed to load sites</p>
        <p className="text-sm text-white/60 mt-1">{error.message}</p>
      </div>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-white/10 text-white hover:bg-white/20 transition-colors"
      >
        <RefreshCw className="w-4 h-4" />
        Try Again
      </button>
    </div>
  );
}

/** Build a duplicate site name like "My Site (Feb 26 @ 3:45 pm)" */
function buildDuplicateName(originalName: string): string {
  const baseName = originalName.replace(/\s*\([^)]*\)\s*$/, '');
  const now = new Date();
  const timestamp = now
    .toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .replace(/\s*(AM|PM)$/i, (m) => m.toLowerCase());
  return `${baseName} (${timestamp})`;
}

/** Best-effort OPFS cleanup — non-blocking, fire-and-forget */
async function cleanupSiteOpfs(siteId: string): Promise<void> {
  const manager = new OpfsManager();
  try {
    await manager.connect();
    await manager.deleteSite(siteId);
  } catch (err) {
    // OPFS cleanup is best-effort. If it fails, the LRU evictor
    // will clean it up when space is needed.
    console.warn(`[SitesGrid] OPFS cleanup failed for site ${siteId}:`, err);
  } finally {
    manager.disconnect();
  }
}

export function SitesGridPage() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [sites, setSites] = useState<Site[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [personalWorkspaceEnabled, setPersonalWorkspaceEnabled] =
    useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [newSiteId, setNewSiteId] = useState<string | null>(null);
  const newSiteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [tryAnotherDesignSite, setTryAnotherDesignSite] = useState<Site | null>(
    null,
  );
  // Subscribe to just the duplicating site's percent — the percent is only
  // displayed on the tile being duplicated, and a whole-map subscription
  // would re-render every tile on each SSE progress tick.
  const { data: duplicatePercent } = useQuery<
    Record<string, number>,
    Error,
    number | undefined
  >({
    queryKey: ['duplicateProgress'],
    initialData: {},
    staleTime: Infinity,
    // No queryFn — data is only written by the SSE event handler
    queryFn: () => ({}),
    select: (progress) => (duplicatingId ? progress[duplicatingId] : undefined),
  });

  useEffect(() => {
    return () => {
      if (newSiteTimerRef.current) clearTimeout(newSiteTimerRef.current);
    };
  }, []);

  // SiteViewPage is the app's one lazy route (it carries the Playground
  // runtime). Anyone on the grid is about to open a site — warm the chunk
  // now so the navigation doesn't wait on the download.
  useEffect(() => {
    import('./SiteViewPage').catch(() => {
      // Prefetch only — the router retries the import on navigation.
    });
  }, []);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate({ to: '/login' });
      return;
    }
    if (!authLoading && isAuthenticated) {
      checkProfileAndLoadSites();
    }
  }, [authLoading, isAuthenticated]);

  const checkProfileAndLoadSites = async () => {
    try {
      setError(null);
      // Start both requests concurrently, but await them in order so error
      // and redirect precedence is exactly as before (profile errors first,
      // terms gate before any sites outcome).
      const profilePromise = fetch(`${API_URL}/api/v2/cloud/me`, {
        credentials: 'include',
      });
      const sitesPromise = fetch(`${API_URL}/api/v2/cloud/sites`, {
        credentials: 'include',
      });
      // Suppress an unhandled rejection if we early-return to /terms below.
      sitesPromise.catch(() => {});

      // Check user profile for terms acceptance
      const profileRes = await profilePromise;

      if (!profileRes.ok) {
        throw new Error('Failed to fetch profile');
      }

      const profile: UserProfile = await profileRes.json();
      setPersonalWorkspaceEnabled(profile.personalWorkspaceEnabled !== false);

      if (!profile.termsAcceptedAt) {
        navigate({ to: '/terms' });
        return;
      }

      const sitesRes = await sitesPromise;

      if (!sitesRes.ok) {
        throw new Error(`Failed to fetch sites: ${sitesRes.status}`);
      }

      const sitesData = await sitesRes.json();
      const loadedSites: Site[] = sitesData.sites || [];

      // If user has no sites, auto-create their first one and navigate to it
      if (
        loadedSites.length === 0 &&
        profile.personalWorkspaceEnabled !== false
      ) {
        const createRes = await fetch(`${API_URL}/api/v2/cloud/sites`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: 'My Site' }),
        });

        if (createRes.ok) {
          const createData = await createRes.json();
          navigate({
            to: '/sites/$siteId',
            params: { siteId: createData.site.id },
          });
          return;
        }
      }

      setSites(loadedSites);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSite = async () => {
    if (!personalWorkspaceEnabled) return;

    setIsCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/v2/cloud/sites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ name: `My Site` }),
      });

      if (!response.ok) {
        throw new Error('Failed to create site');
      }

      const data = await response.json();
      // Navigate to the new site
      navigate({ to: '/sites/$siteId', params: { siteId: data.site.id } });
    } catch (err) {
      console.error('Error creating site:', err);
      setError(err instanceof Error ? err : new Error('Failed to create site'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteSite = async (siteId: string) => {
    try {
      const response = await fetch(`${API_URL}/api/v2/cloud/sites/${siteId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete site');
      }

      // Remove from local state
      setSites((prev) => prev.filter((s) => s.id !== siteId));

      // Clean up OPFS data and localStorage markers (best-effort, non-blocking)
      clearSiteMarkers(siteId);
      cleanupSiteOpfs(siteId);
    } catch (err) {
      console.error('Error deleting site:', err);
      setError(err instanceof Error ? err : new Error('Failed to delete site'));
    }
  };

  const handleRenameSite = async (siteId: string, newName: string) => {
    try {
      const response = await fetch(`${API_URL}/api/v2/cloud/sites/${siteId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName }),
      });

      if (!response.ok) {
        throw new Error('Failed to rename site');
      }

      // Update local state
      setSites((prev) =>
        prev.map((s) => (s.id === siteId ? { ...s, name: newName } : s)),
      );
    } catch (err) {
      console.error('Error renaming site:', err);
      setError(err instanceof Error ? err : new Error('Failed to rename site'));
    }
  };

  const handleDuplicateSite = async (siteId: string) => {
    const sourceSite = sites.find((s) => s.id === siteId);
    setDuplicatingId(siteId);
    try {
      const duplicateName = buildDuplicateName(sourceSite?.name || 'Site');

      const response = await fetch(
        `${API_URL}/api/v2/cloud/sites/${siteId}/duplicate`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: duplicateName }),
        },
      );

      if (!response.ok) {
        let errorMessage = 'Failed to duplicate site';
        try {
          const data = await response.json();
          if (data.error) errorMessage = data.error;
        } catch {
          // Response wasn't JSON (e.g., proxy error page)
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (!data.success || !data.siteId) {
        throw new Error(data.error || 'Failed to duplicate site');
      }

      // Build a site object from the response + source site data
      const newSite: Site = {
        id: data.siteId,
        name: duplicateName,
        url: data.siteUrl || '',
        createdAt: new Date().toISOString(),
        lastSeenAt: new Date().toISOString(),
        isActive: true,
        heroPreviews: sourceSite?.heroPreviews,
        selectedHeroId: sourceSite?.selectedHeroId,
        siteSlug: undefined,
        messageCount: 0,
      };

      if (newSiteTimerRef.current) clearTimeout(newSiteTimerRef.current);
      setNewSiteId(newSite.id);
      setSites((prev) => [newSite, ...prev]);
      newSiteTimerRef.current = setTimeout(() => setNewSiteId(null), 500);
    } catch (err) {
      console.error('Error duplicating site:', err);
      setError(
        err instanceof Error ? err : new Error('Failed to duplicate site'),
      );
    } finally {
      setDuplicatingId(null);
      // Clear progress for this site from the cache
      queryClient.setQueryData<Record<string, number>>(
        ['duplicateProgress'],
        (old) => {
          if (!old) return {};
          const next = { ...old };
          delete next[siteId];
          return next;
        },
      );
    }
  };

  const handleSelectSite = (siteId: string) => {
    navigate({ to: '/sites/$siteId', params: { siteId } });
  };

  // Sort sites by most recently updated. Memoized, with each timestamp
  // parsed once per site instead of twice per comparison on every render.
  const sortedSites = useMemo(
    () =>
      sites
        .map(
          (s) => [new Date(s.lastSeenAt || s.createdAt).getTime(), s] as const,
        )
        .sort((a, b) => b[0] - a[0])
        .map(([, s]) => s),
    [sites],
  );

  const hasSites = sortedSites.length > 0;
  const showEmptyState = !isLoading && !error && !hasSites;

  if (authLoading || isLoading) {
    return (
      <div className="h-dvh flex flex-col px-4 pb-4 pt-2 gap-2">
        <div className="h-12 shrink-0" />
        <div className="miles-container flex-1 min-h-0 flex items-center justify-center">
          <PulsingDot />
        </div>
      </div>
    );
  }

  return (
    <div className="h-dvh flex flex-col px-4 pb-4 pt-2 gap-2">
      {/* Header - outside the container */}
      <SitesGridHeader
        onNewSite={handleCreateSite}
        disabled={isCreating || !personalWorkspaceEnabled}
        user={user}
      />

      <MobileNotice />
      <BetaWidget />

      {/* Main content container with Miles styling - fills remaining height */}
      <div className="miles-container flex-1 min-h-0 flex flex-col">
        <div className="flex-1 overflow-auto p-6 scrollbar-thin">
          {/* Error state */}
          {error && (
            <SitesGridError
              error={error}
              onRetry={() => checkProfileAndLoadSites()}
            />
          )}

          {/* Empty state for new users */}
          {showEmptyState && (
            <EmptyState
              onNewSite={handleCreateSite}
              disabled={isCreating || !personalWorkspaceEnabled}
              personalWorkspaceEnabled={personalWorkspaceEnabled}
            />
          )}

          {/* Sites grid - responsive 1-3 columns */}
          {hasSites && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {sortedSites.map((site) => (
                <SiteTile
                  key={site.id}
                  site={site}
                  isDuplicating={duplicatingId === site.id}
                  duplicatePercent={
                    duplicatingId === site.id ? duplicatePercent : undefined
                  }
                  isNew={newSiteId === site.id}
                  onClick={() => handleSelectSite(site.id)}
                  onDelete={() => handleDeleteSite(site.id)}
                  onRename={(newName) => handleRenameSite(site.id, newName)}
                  onDuplicate={() => handleDuplicateSite(site.id)}
                  onTryAnotherDesign={() => setTryAnotherDesignSite(site)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {tryAnotherDesignSite && (
        <TryAnotherDesignModal
          open={!!tryAnotherDesignSite}
          onOpenChange={(open) => {
            if (!open) setTryAnotherDesignSite(null);
          }}
          site={tryAnotherDesignSite}
          onSiteCreated={(siteId) => {
            setTryAnotherDesignSite(null);
            navigate({ to: '/sites/$siteId', params: { siteId } });
          }}
        />
      )}
    </div>
  );
}
